import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface ExitLink {
    url: string;
    domain: string;
    visits: number;
    users: number;
    percentage: number;
}

interface TrafficSource {
    source: string;
    engine: string;
    visits: number;
    users: number;
    bounceRate: string;
    avgDuration: number;
}

interface GeoLocation {
    country: string;
    city: string;
    visits: number;
    users: number;
    coordinates: { lat: number; lng: number } | null;
}

interface CountryData {
    country: string;
    visits: number;
    users: number;
    cities: { name: string; visits: number; users: number }[];
}

interface SessionPage {
    url: string;
    title: string;
    pageviews: number;
    users: number;
}

interface SessionFilters {
    source: string;
    country: string;
    device: string;
    entryPage: string;
}

interface Props {
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
    dateRange?: 'month' | 'week' | 'custom';
}

const translations = {
    en: {
        exitLinks: 'Exit Links',
        exitLinksDesc: 'Where visitors go when leaving',
        trafficSources: 'Traffic Sources',
        trafficSourcesDesc: 'Where visitors come from',
        demographicMap: 'Geographic Demographics',
        demographicMapDesc: 'Visitors by country and city',
        domain: 'Domain',
        visits: 'Visits',
        users: 'Users',
        bounceRate: 'Bounce Rate',
        avgDuration: 'Avg. Duration',
        source: 'Source',
        engine: 'Engine',
        country: 'Country',
        city: 'City',
        loading: 'Loading...',
        noData: 'No data available',
        noDataFlow: 'No Data Flow',
        noDataFlowDesc: 'Yandex Metrica API is not connected. Configure your API token to see real analytics data.',
        reconnect: 'Reconnect',
        configureApi: 'Configure API',
        liveVisitors: 'Live Visitors',
        organic: 'Organic',
        direct: 'Direct',
        referral: 'Referral',
        social: 'Social',
        ads: 'Ads',
        other: 'Other',
        sessions: 'Visitor Sessions',
        sessionsDesc: 'Detailed list of all visits',
        today: 'Today',
        yesterday: 'Yesterday',
        week: 'Week',
        time: 'Time',
        activity: 'Activity',
        duration: 'Duration',
        pages: 'Pages',
        page: 'Page',
        device: 'Device',
        location: 'Location',
        showMore: 'Show More',
        replay: 'Replay',
        filter: 'Filter',
        clearFilters: 'Clear Filters',
        allSources: 'All Sources',
        allCountries: 'All Countries',
        allDevices: 'All Devices',
        allPages: 'All Pages',
        totalSessions: 'Total Sessions',
        avgPages: 'Avg Pages',
        bounceRateLabel: 'Bounce Rate',
        browsedPages: 'Browsed Pages',
        noPages: 'No page data available',
        loadingPages: 'Loading pages...',
        viewInMetrika: 'View in Metrika'
    },
    sv: {
        exitLinks: 'Utgångslänkar',
        exitLinksDesc: 'Vart besökare går när de lämnar',
        trafficSources: 'Trafikkällor',
        trafficSourcesDesc: 'Varifrån besökare kommer',
        demographicMap: 'Geografisk demografi',
        demographicMapDesc: 'Besökare per land och stad',
        domain: 'Domän',
        visits: 'Besök',
        users: 'Användare',
        bounceRate: 'Avvisningsfrekvens',
        avgDuration: 'Snitt varaktighet',
        source: 'Källa',
        engine: 'Motor',
        country: 'Land',
        city: 'Stad',
        loading: 'Laddar...',
        noData: 'Ingen data tillgänglig',
        noDataFlow: 'Veri Akışı Yok',
        noDataFlowDesc: 'Yandex Metrica API bağlantısı yok. Gerçek analiz verilerini görmek için API tokeninizi yapılandırın.',
        reconnect: 'Tekrar Bağlan',
        configureApi: 'API Yapılandır',
        liveVisitors: 'Aktiva besökare',
        organic: 'Organisk',
        direct: 'Direkt',
        referral: 'Hänvisning',
        social: 'Sociala medier',
        ads: 'Annonser',
        other: 'Övrigt',
        sessions: 'Besökarsessioner',
        sessionsDesc: 'Detaljerad lista över alla besök',
        today: 'Idag',
        yesterday: 'Igår',
        week: 'Vecka',
        time: 'Tid',
        activity: 'Aktivitet',
        duration: 'Längd',
        pages: 'Sidor',
        page: 'Sida',
        device: 'Enhet',
        location: 'Plats',
        showMore: 'Visa mer',
        replay: 'Spela upp',
        filter: 'Filter',
        clearFilters: 'Rensa filter',
        allSources: 'Alla källor',
        allCountries: 'Alla länder',
        allDevices: 'Alla enheter',
        allPages: 'Alla sidor',
        totalSessions: 'Totala sessioner',
        avgPages: 'Snittsidor',
        bounceRateLabel: 'Avvisningsfrekvens',
        browsedPages: 'Besökta sidor',
        noPages: 'Ingen siddata tillgänglig',
        loadingPages: 'Laddar sidor...',
        viewInMetrika: 'Visa i Metrika'
    }
};

const COLORS = ['#14b8a6', '#8b5cf6', '#f59e0b', '#ec4899', '#3b82f6', '#10b981', '#ef4444'];

const YandexAnalytics: React.FC<Props> = ({ theme = 'dark', language = 'en', dateRange = 'week' }) => {
    const t = translations[language];
    const isDark = theme === 'dark';

    const [exitLinks, setExitLinks] = useState<ExitLink[]>([]);
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const [geoLocations, setGeoLocations] = useState<GeoLocation[]>([]);
    const [countryData, setCountryData] = useState<CountryData[]>([]);
    const [sourceGroups, setSourceGroups] = useState<Record<string, TrafficSource[]>>({});
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'map' | 'exitLinks' | 'sources' | 'sessions'>('map');

    // Sessions state
    interface SessionEntry {
        id: number;
        visitDate: string;
        visitTime: string;
        startUrl: string;
        source: string;
        os: string;
        browser: string;
        device: string;
        country: string;
        city: string;
        visits: number;
        pageviews: number;
        duration: number;
        bounceRate: number;
        activityLevel: number;
    }
    const [sessions, setSessions] = useState<SessionEntry[]>([]);
    const [sessionsTotal, setSessionsTotal] = useState(0);
    const [sessionsDateFilter, setSessionsDateFilter] = useState<'today' | 'yesterday' | 'week'>('today');
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsOffset, setSessionsOffset] = useState(0);
    const SESSIONS_LIMIT = 30;
    const [counterId, setCounterId] = useState<string>('106643915');
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [sessionPages, setSessionPages] = useState<Record<number, SessionPage[]>>({});
    const [loadingPages, setLoadingPages] = useState<Record<number, boolean>>({});
    const [sessionFilters, setSessionFilters] = useState<SessionFilters>({ source: '', country: '', device: '', entryPage: '' });
    const [showFilters, setShowFilters] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Compute date range for API calls
    const { startDate, endDate } = useMemo(() => {
        const end = new Date();
        const endStr = end.toISOString().split('T')[0];
        let start: Date;
        if (dateRange === 'month') {
            start = new Date(end.getFullYear(), end.getMonth(), 1);
        } else {
            // 'week' or 'custom' default to last 7 days
            start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        }
        return { startDate: start.toISOString().split('T')[0], endDate: endStr };
    }, [dateRange]);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const dateParams = `startDate=${startDate}&endDate=${endDate}`;
                const [exitRes, sourcesRes, geoRes] = await Promise.all([
                    fetch(`/api/analytics/yandex/exit-links?${dateParams}`),
                    fetch(`/api/analytics/yandex/traffic-sources?${dateParams}`),
                    fetch(`/api/analytics/yandex/geo-demographics?${dateParams}`)
                ]);

                const exitData = await exitRes.json();
                const sourcesData = await sourcesRes.json();
                const geoData = await geoRes.json();

                // Check for API errors (no token configured, etc.)
                const errors = [exitData, sourcesData, geoData].filter(d => d.success === false);
                if (errors.length > 0) {
                    setApiError(errors[0].error || 'API connection failed');
                } else {
                    setApiError(null);
                }

                if (exitData.success !== false) {
                    setExitLinks(exitData.exitLinks || []);
                }
                if (sourcesData.success !== false) {
                    setTrafficSources(sourcesData.sources || []);
                    setSourceGroups(sourcesData.grouped || {});
                }
                if (geoData.success !== false) {
                    setGeoLocations(geoData.locations || []);
                    setCountryData(geoData.byCountry || []);
                }
            } catch (error) {
                console.error('Failed to fetch Yandex analytics:', error);
                setApiError('Network error: Unable to reach the analytics API');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // Refresh every 60 seconds
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [startDate, endDate]);

    // Fetch sessions when tab or filter changes
    useEffect(() => {
        if (activeTab !== 'sessions') return;

        const fetchSessions = async () => {
            setSessionsLoading(true);
            try {
                const res = await fetch(`/api/analytics/yandex/visitor-sessions?date=${sessionsDateFilter}&limit=${SESSIONS_LIMIT}&offset=${sessionsOffset}`);
                const data = await res.json();
                if (data.success !== false) {
                    if (sessionsOffset > 0) {
                        setSessions(prev => [...prev, ...(data.sessions || [])]);
                    } else {
                        setSessions(data.sessions || []);
                    }
                    setSessionsTotal(data.total || 0);
                    if (data.counterId) setCounterId(data.counterId);
                    setApiError(null);
                } else {
                    setApiError(data.error || 'API connection failed');
                }
            } catch (error) {
                console.error('Failed to fetch sessions:', error);
            } finally {
                setSessionsLoading(false);
            }
        };

        fetchSessions();
    }, [activeTab, sessionsDateFilter, sessionsOffset]);

    // Reset offset when date filter changes
    useEffect(() => {
        setSessionsOffset(0);
    }, [sessionsDateFilter]);

    // Helper: format duration
    const formatDuration = (seconds: number): string => {
        if (seconds < 60) return `${seconds}s`;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        if (m < 60) return `${m}m ${s}s`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m`;
    };

    // Helper: OS icon
    const getOSIcon = (os: string): string => {
        const lower = os.toLowerCase();
        if (lower.includes('windows')) return '🪟';
        if (lower.includes('mac') || lower.includes('ios')) return '🍎';
        if (lower.includes('android')) return '🤖';
        if (lower.includes('linux')) return '🐧';
        return '💻';
    };

    // Helper: Browser icon
    const getBrowserIcon = (browser: string): string => {
        const lower = browser.toLowerCase();
        if (lower.includes('chrome')) return '🌐';
        if (lower.includes('safari')) return '🧭';
        if (lower.includes('firefox')) return '🦊';
        if (lower.includes('edge')) return '🔷';
        if (lower.includes('samsung')) return '📱';
        return '🌍';
    };

    // Helper: Device icon
    const getDeviceIcon = (device: string): string => {
        const lower = device.toLowerCase();
        if (lower.includes('mobile')) return '📱';
        if (lower.includes('tablet')) return '📋';
        return '🖥️';
    };

    // Helper: Activity dots
    const getActivityDots = (level: number) => {
        const colors = ['#ef4444', '#f59e0b', '#22c55e', '#14b8a6'];
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4].map(i => (
                    <div
                        key={i}
                        className="w-1.5 h-3 rounded-sm"
                        style={{ backgroundColor: i <= level ? colors[level - 1] : isDark ? '#334155' : '#e2e8f0' }}
                    />
                ))}
            </div>
        );
    };

    // Expand row and load pages
    const toggleExpandRow = async (index: number, session: SessionEntry) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
            setExpandedRows(newExpanded);
            return;
        }
        newExpanded.add(index);
        setExpandedRows(newExpanded);

        if (!sessionPages[index]) {
            setLoadingPages(prev => ({ ...prev, [index]: true }));
            try {
                const params = new URLSearchParams({
                    date: session.visitDate,
                    ...(session.source && session.source !== 'direct' ? { source: session.source } : {}),
                    ...(session.country ? { country: session.country } : {}),
                    ...(session.city ? { city: session.city } : {})
                });
                const res = await fetch(`/api/analytics/yandex/session-pages?${params}`);
                const data = await res.json();
                setSessionPages(prev => ({ ...prev, [index]: data.pages || [] }));
            } catch {
                setSessionPages(prev => ({ ...prev, [index]: [] }));
            } finally {
                setLoadingPages(prev => ({ ...prev, [index]: false }));
            }
        }
    };

    // Filtered sessions
    const filteredSessions = useMemo(() => {
        return sessions.filter(s => {
            if (sessionFilters.source && s.source !== sessionFilters.source) return false;
            if (sessionFilters.country && s.country !== sessionFilters.country) return false;
            if (sessionFilters.device && s.device !== sessionFilters.device) return false;
            if (sessionFilters.entryPage && !s.startUrl.includes(sessionFilters.entryPage)) return false;
            return true;
        });
    }, [sessions, sessionFilters]);

    // Unique filter options
    const filterOptions = useMemo(() => ({
        sources: [...new Set(sessions.map(s => s.source))].filter(Boolean).sort(),
        countries: [...new Set(sessions.map(s => s.country))].filter(s => s !== 'Unknown').sort(),
        devices: [...new Set(sessions.map(s => s.device))].filter(Boolean).sort(),
        entryPages: [...new Set(sessions.map(s => {
            try { return new URL(s.startUrl, 'https://lumitours.se').pathname; } catch { return s.startUrl; }
        }))].sort()
    }), [sessions]);

    // Session infographic data
    const sessionStats = useMemo(() => {
        const fs = filteredSessions;
        if (fs.length === 0) return null;
        const totalDuration = fs.reduce((sum, s) => sum + s.duration, 0);
        const totalPages = fs.reduce((sum, s) => sum + s.pageviews, 0);
        const totalBounce = fs.reduce((sum, s) => sum + s.bounceRate, 0);
        return {
            total: fs.length,
            avgDuration: Math.round(totalDuration / fs.length),
            avgPages: (totalPages / fs.length).toFixed(1),
            bounceRate: (totalBounce / fs.length).toFixed(1)
        };
    }, [filteredSessions]);

    const countryPieData = useMemo(() => {
        const grouped: Record<string, number> = {};
        filteredSessions.forEach(s => { grouped[s.country] = (grouped[s.country] || 0) + 1; });
        return Object.entries(grouped).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([name, value]) => ({ name, value }));
    }, [filteredSessions]);

    const deviceBarData = useMemo(() => {
        const grouped: Record<string, number> = {};
        filteredSessions.forEach(s => { grouped[s.device] = (grouped[s.device] || 0) + 1; });
        return Object.entries(grouped).map(([name, value]) => ({ name, value }));
    }, [filteredSessions]);

    const sourcePieDataSessions = useMemo(() => {
        const grouped: Record<string, number> = {};
        filteredSessions.forEach(s => { grouped[s.source || 'direct'] = (grouped[s.source || 'direct'] || 0) + 1; });
        return Object.entries(grouped).map(([name, value]) => ({ name, value }));
    }, [filteredSessions]);

    const hasActiveFilters = Object.values(sessionFilters).some(v => v !== '');

    // Animated Map
    useEffect(() => {
        if (!svgRef.current || !wrapperRef.current || activeTab !== 'map' || geoLocations.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const { width, height } = wrapperRef.current.getBoundingClientRect();

        const projection = d3.geoMercator()
            .scale(width / 6)
            .translate([width / 2, height / 1.4]);

        const path = d3.geoPath().projection(projection);

        // Load world map
        d3.json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
            .then((world: any) => {
                if (!world) return;

                const countries = topojson.feature(world, world.objects.countries);

                // Draw countries
                svg.append("g")
                    .selectAll("path")
                    .data((countries as any).features)
                    .enter().append("path")
                    .attr("d", path as any)
                    .attr("fill", isDark ? "#1e293b" : "#e2e8f0")
                    .attr("stroke", isDark ? "#334155" : "#cbd5e1")
                    .attr("stroke-width", 0.5);

                drawLocations(svg, projection);
            })
            .catch(() => {
                // Fallback without map
                drawLocations(svg, projection);
            });

        function drawLocations(svgSelection: any, proj: any) {
            const maxVisits = Math.max(...geoLocations.map(l => l.visits));

            geoLocations.forEach((location, index) => {
                if (!location.coordinates) return;

                const coords = proj([location.coordinates.lng, location.coordinates.lat]);
                if (!coords) return;

                const radius = 4 + (location.visits / maxVisits) * 12;
                const delay = index * 100;

                const group = svgSelection.append("g")
                    .attr("transform", `translate(${coords[0]}, ${coords[1]})`)
                    .style("cursor", "pointer");

                // Animated pulse ring
                group.append("circle")
                    .attr("r", radius)
                    .attr("fill", "rgba(20, 184, 166, 0.3)")
                    .attr("stroke", "none")
                    .style("opacity", 0)
                    .transition()
                    .delay(delay)
                    .duration(500)
                    .style("opacity", 1)
                    .on("end", function repeat() {
                        d3.select(this)
                            .transition()
                            .duration(1500)
                            .attr("r", radius + 10)
                            .style("opacity", 0)
                            .transition()
                            .duration(0)
                            .attr("r", radius)
                            .style("opacity", 0.5)
                            .on("end", repeat);
                    });

                // Main dot
                group.append("circle")
                    .attr("r", 0)
                    .attr("fill", "#14b8a6")
                    .attr("stroke", isDark ? "#fff" : "#1f2937")
                    .attr("stroke-width", 1.5)
                    .transition()
                    .delay(delay)
                    .duration(400)
                    .ease(d3.easeElastic)
                    .attr("r", radius);

                // Label (country + city)
                const labelGroup = group.append("g")
                    .attr("transform", `translate(${radius + 5}, -5)`)
                    .style("opacity", 0);

                labelGroup.append("rect")
                    .attr("x", -2)
                    .attr("y", -10)
                    .attr("width", 100)
                    .attr("height", 24)
                    .attr("rx", 3)
                    .attr("fill", isDark ? "rgba(15, 23, 42, 0.9)" : "rgba(255, 255, 255, 0.95)")
                    .attr("stroke", isDark ? "#334155" : "#e2e8f0");

                labelGroup.append("text")
                    .attr("fill", isDark ? "#e2e8f0" : "#1f2937")
                    .attr("font-size", "10px")
                    .attr("font-weight", "600")
                    .text(`${location.city}, ${location.country.substring(0, 3).toUpperCase()}`);

                labelGroup.append("text")
                    .attr("y", 10)
                    .attr("fill", "#14b8a6")
                    .attr("font-size", "9px")
                    .text(`${location.visits} ${t.visits.toLowerCase()}`);

                // Show label on hover
                group
                    .on("mouseenter", () => labelGroup.transition().duration(200).style("opacity", 1))
                    .on("mouseleave", () => labelGroup.transition().duration(200).style("opacity", 0));
            });
        }

    }, [geoLocations, activeTab, isDark, t.visits]);

    // Source type pie chart data
    const sourcePieData = useMemo(() => {
        const grouped: Record<string, number> = {};
        trafficSources.forEach(s => {
            const type = s.source || 'other';
            grouped[type] = (grouped[type] || 0) + s.visits;
        });
        return Object.entries(grouped).map(([name, value]) => ({ name, value }));
    }, [trafficSources]);

    const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
    const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';
    const textColor = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
    const hoverBg = isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50';

    if (loading) {
        return (
            <div className={`${bgColor} rounded-xl p-8 flex items-center justify-center border ${borderColor}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
                <span className={`ml-3 ${textSecondary}`}>{t.loading}</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Tab Navigation */}
            <div className={`flex gap-2 p-1 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                {([
                    { id: 'map' as const, label: t.demographicMap, icon: '🗺️' },
                    { id: 'exitLinks' as const, label: t.exitLinks, icon: '↗️' },
                    { id: 'sources' as const, label: t.trafficSources, icon: '📊' },
                    { id: 'sessions' as const, label: t.sessions, icon: '👥' }
                ]).map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${activeTab === tab.id
                            ? 'bg-teal-600 text-white shadow-lg'
                            : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        <span>{tab.icon}</span>
                        <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* API Error Warning Banner */}
            {apiError && (
                <div className={`rounded-xl border-2 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${isDark ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                    }`}>
                    <div className="flex items-start gap-3">
                        <span className="text-2xl mt-0.5">⚠️</span>
                        <div>
                            <h4 className={`font-bold text-sm ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                                {t.noDataFlow}
                            </h4>
                            <p className={`text-sm mt-1 ${isDark ? 'text-red-300/80' : 'text-red-600'}`}>
                                {t.noDataFlowDesc}
                            </p>
                            <p className={`text-xs mt-1 font-mono ${isDark ? 'text-red-400/60' : 'text-red-500/70'}`}>
                                {apiError}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setApiError(null);
                            setLoading(true);
                            // Re-trigger data fetch by forcing a re-render
                            window.location.reload();
                        }}
                        className={`flex-shrink-0 px-5 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${isDark
                            ? 'bg-red-700/50 hover:bg-red-700/80 text-red-200 border border-red-600/50'
                            : 'bg-red-100 hover:bg-red-200 text-red-700 border border-red-300'
                            }`}
                    >
                        🔄 {t.reconnect}
                    </button>
                </div>
            )}

            {/* Map Tab */}
            {activeTab === 'map' && (
                <div className={`${bgColor} rounded-xl border ${borderColor} overflow-hidden`}>
                    <div className="p-4 border-b border-slate-700/30">
                        <h3 className={`text-lg font-bold ${textColor}`}>{t.demographicMap}</h3>
                        <p className={`text-sm ${textSecondary}`}>{t.demographicMapDesc}</p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-0">
                        {/* Map */}
                        <div className="lg:col-span-2" ref={wrapperRef} style={{ height: '400px' }}>
                            <svg ref={svgRef} width="100%" height="100%" className="cursor-crosshair"></svg>
                        </div>

                        {/* Country list with cities */}
                        <div className={`border-l ${borderColor} max-h-[400px] overflow-y-auto`}>
                            <div className={`p-3 sticky top-0 ${bgColor} border-b ${borderColor}`}>
                                <h4 className={`font-semibold text-sm ${textColor}`}>{t.country} / {t.city}</h4>
                            </div>
                            <div className="divide-y divide-slate-700/30">
                                {countryData.map((country, i) => (
                                    <div key={i} className={`${hoverBg} transition-colors`}>
                                        <div className="px-3 py-2 flex justify-between items-center">
                                            <span className={`font-medium text-sm ${textColor}`}>
                                                🏳️ {country.country}
                                            </span>
                                            <span className="text-teal-400 font-bold text-sm">{country.visits}</span>
                                        </div>
                                        <div className={`px-3 pb-2 space-y-1 ${textSecondary}`}>
                                            {country.cities.slice(0, 3).map((city, j) => (
                                                <div key={j} className="flex justify-between text-xs pl-4">
                                                    <span>📍 {city.name}</span>
                                                    <span>{city.visits}</span>
                                                </div>
                                            ))}
                                            {country.cities.length > 3 && (
                                                <div className="text-xs pl-4 text-teal-500">
                                                    +{country.cities.length - 3} more cities
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit Links Tab */}
            {activeTab === 'exitLinks' && (
                <div className={`${bgColor} rounded-xl border ${borderColor} overflow-hidden`}>
                    <div className="p-4 border-b border-slate-700/30">
                        <h3 className={`text-lg font-bold ${textColor}`}>{t.exitLinks}</h3>
                        <p className={`text-sm ${textSecondary}`}>{t.exitLinksDesc}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-0">
                        {/* Bar Chart */}
                        <div className="p-4 h-[350px]">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                                <BarChart
                                    data={exitLinks.slice(0, 8)}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                                    <XAxis type="number" stroke={isDark ? '#94a3b8' : '#64748b'} />
                                    <YAxis
                                        dataKey="domain"
                                        type="category"
                                        stroke={isDark ? '#94a3b8' : '#64748b'}
                                        tick={{ fontSize: 11 }}
                                        width={80}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            borderColor: isDark ? '#334155' : '#e2e8f0',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Bar dataKey="visits" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Table */}
                        <div className={`border-l ${borderColor} overflow-auto max-h-[350px]`}>
                            <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <tr>
                                        <th className={`text-left p-3 ${textColor}`}>{t.domain}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.visits}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.users}</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                    {exitLinks.map((link, i) => (
                                        <tr key={i} className={hoverBg}>
                                            <td className={`p-3 ${textColor}`}>
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="hover:text-teal-400 flex items-center gap-2"
                                                >
                                                    <span className="text-lg">🔗</span>
                                                    {link.domain}
                                                </a>
                                            </td>
                                            <td className="p-3 text-right text-teal-400 font-medium">{link.visits}</td>
                                            <td className={`p-3 text-right ${textSecondary}`}>{link.users}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Traffic Sources Tab */}
            {activeTab === 'sources' && (
                <div className={`${bgColor} rounded-xl border ${borderColor} overflow-hidden`}>
                    <div className="p-4 border-b border-slate-700/30">
                        <h3 className={`text-lg font-bold ${textColor}`}>{t.trafficSources}</h3>
                        <p className={`text-sm ${textSecondary}`}>{t.trafficSourcesDesc}</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-0">
                        {/* Pie Chart */}
                        <div className="p-4 h-[300px]">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                                <PieChart>
                                    <Pie
                                        data={sourcePieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        labelLine={false}
                                    >
                                        {sourcePieData.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            borderColor: isDark ? '#334155' : '#e2e8f0',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Full Table */}
                        <div className={`md:col-span-2 border-l ${borderColor} overflow-auto max-h-[300px]`}>
                            <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <tr>
                                        <th className={`text-left p-3 ${textColor}`}>{t.source}</th>
                                        <th className={`text-left p-3 ${textColor}`}>{t.engine}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.visits}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.bounceRate}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.avgDuration}</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                    {trafficSources.map((source, i) => (
                                        <tr key={i} className={hoverBg}>
                                            <td className={`p-3 ${textColor}`}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span className={`w-2 h-2 rounded-full`} style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                                                    {source.source}
                                                </span>
                                            </td>
                                            <td className={`p-3 ${textSecondary}`}>{source.engine || '-'}</td>
                                            <td className="p-3 text-right text-teal-400 font-medium">{source.visits}</td>
                                            <td className={`p-3 text-right ${textSecondary}`}>{source.bounceRate}%</td>
                                            <td className={`p-3 text-right ${textSecondary}`}>{Math.floor(source.avgDuration / 60)}m {source.avgDuration % 60}s</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
                <div className="space-y-4">
                    {/* Header with date filter */}
                    <div className={`${bgColor} rounded-xl border ${borderColor} p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3`}>
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className={`text-lg font-bold ${textColor}`}>👥 {t.sessions}</h3>
                                <p className={`text-sm ${textSecondary}`}>{t.sessionsDesc}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                                {(['today', 'yesterday', 'week'] as const).map(filter => (
                                    <button
                                        key={filter}
                                        onClick={() => setSessionsDateFilter(filter)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sessionsDateFilter === filter
                                            ? 'bg-teal-600 text-white'
                                            : isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {t[filter]}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${showFilters || hasActiveFilters
                                    ? 'bg-purple-600 text-white'
                                    : isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                🔍 {t.filter} {hasActiveFilters ? `(${Object.values(sessionFilters).filter(v => v).length})` : ''}
                            </button>
                            <a
                                href={`https://metrika.yandex.com/dashboard?id=${counterId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                                🎥 {t.viewInMetrika}
                            </a>
                        </div>
                    </div>

                    {/* Filter Bar */}
                    {showFilters && (
                        <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <label className={`text-xs font-medium block mb-1 ${textSecondary}`}>{t.source}</label>
                                    <select
                                        value={sessionFilters.source}
                                        onChange={e => setSessionFilters(f => ({ ...f, source: e.target.value }))}
                                        className={`w-full px-3 py-2 rounded-lg text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                                    >
                                        <option value="">{t.allSources}</option>
                                        {filterOptions.sources.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={`text-xs font-medium block mb-1 ${textSecondary}`}>{t.country}</label>
                                    <select
                                        value={sessionFilters.country}
                                        onChange={e => setSessionFilters(f => ({ ...f, country: e.target.value }))}
                                        className={`w-full px-3 py-2 rounded-lg text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                                    >
                                        <option value="">{t.allCountries}</option>
                                        {filterOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={`text-xs font-medium block mb-1 ${textSecondary}`}>{t.device}</label>
                                    <select
                                        value={sessionFilters.device}
                                        onChange={e => setSessionFilters(f => ({ ...f, device: e.target.value }))}
                                        className={`w-full px-3 py-2 rounded-lg text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                                    >
                                        <option value="">{t.allDevices}</option>
                                        {filterOptions.devices.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={`text-xs font-medium block mb-1 ${textSecondary}`}>{t.page}</label>
                                    <select
                                        value={sessionFilters.entryPage}
                                        onChange={e => setSessionFilters(f => ({ ...f, entryPage: e.target.value }))}
                                        className={`w-full px-3 py-2 rounded-lg text-xs border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                                    >
                                        <option value="">{t.allPages}</option>
                                        {filterOptions.entryPages.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>
                            {hasActiveFilters && (
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    {Object.entries(sessionFilters).filter(([, v]) => v).map(([key, value]) => (
                                        <span key={key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs">
                                            {value}
                                            <button onClick={() => setSessionFilters(f => ({ ...f, [key]: '' }))} className="hover:text-white ml-0.5">×</button>
                                        </span>
                                    ))}
                                    <button
                                        onClick={() => setSessionFilters({ source: '', country: '', device: '', entryPage: '' })}
                                        className="text-xs text-red-400 hover:text-red-300 underline"
                                    >
                                        {t.clearFilters}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Infographic Cards */}
                    {sessionStats && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <div className={`text-xs font-medium ${textSecondary} mb-1`}>{t.totalSessions}</div>
                                <div className={`text-2xl font-bold ${textColor}`}>{sessionStats.total}</div>
                                <div className="mt-2 h-1 rounded-full bg-teal-500/20"><div className="h-1 rounded-full bg-teal-500" style={{ width: '100%' }} /></div>
                            </div>
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <div className={`text-xs font-medium ${textSecondary} mb-1`}>{t.avgDuration}</div>
                                <div className={`text-2xl font-bold ${textColor}`}>{formatDuration(sessionStats.avgDuration)}</div>
                                <div className="mt-2 h-1 rounded-full bg-purple-500/20"><div className="h-1 rounded-full bg-purple-500" style={{ width: `${Math.min(100, sessionStats.avgDuration / 3)}%` }} /></div>
                            </div>
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <div className={`text-xs font-medium ${textSecondary} mb-1`}>{t.avgPages}</div>
                                <div className={`text-2xl font-bold ${textColor}`}>{sessionStats.avgPages}</div>
                                <div className="mt-2 h-1 rounded-full bg-blue-500/20"><div className="h-1 rounded-full bg-blue-500" style={{ width: `${Math.min(100, Number(sessionStats.avgPages) * 10)}%` }} /></div>
                            </div>
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <div className={`text-xs font-medium ${textSecondary} mb-1`}>{t.bounceRateLabel}</div>
                                <div className={`text-2xl font-bold ${Number(sessionStats.bounceRate) > 50 ? 'text-red-400' : 'text-green-400'}`}>{sessionStats.bounceRate}%</div>
                                <div className="mt-2 h-1 rounded-full bg-red-500/20"><div className="h-1 rounded-full bg-red-500" style={{ width: `${sessionStats.bounceRate}%` }} /></div>
                            </div>
                        </div>
                    )}

                    {/* Charts Row */}
                    {filteredSessions.length > 0 && (
                        <div className="grid md:grid-cols-3 gap-3">
                            {/* Country Distribution */}
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <h4 className={`text-xs font-semibold mb-3 ${textSecondary}`}>🌍 {t.country}</h4>
                                <div className="h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
                                        <PieChart>
                                            <Pie data={countryPieData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value"
                                                label={({ name, percent }) => `${name.substring(0, 8)} ${(percent * 100).toFixed(0)}%`}
                                                labelLine={false}
                                            >
                                                {countryPieData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', borderColor: isDark ? '#334155' : '#e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Device Distribution */}
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <h4 className={`text-xs font-semibold mb-3 ${textSecondary}`}>📱 {t.device}</h4>
                                <div className="h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
                                        <BarChart data={deviceBarData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                                            <YAxis tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }} />
                                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', borderColor: isDark ? '#334155' : '#e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                                            <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Source Distribution */}
                            <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
                                <h4 className={`text-xs font-semibold mb-3 ${textSecondary}`}>📊 {t.source}</h4>
                                <div className="h-[180px]">
                                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100}>
                                        <PieChart>
                                            <Pie data={sourcePieDataSessions} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} dataKey="value"
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                labelLine={false}
                                            >
                                                {sourcePieDataSessions.map((_, idx) => <Cell key={idx} fill={COLORS[(idx + 2) % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', borderColor: isDark ? '#334155' : '#e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sessions Table */}
                    <div className={`${bgColor} rounded-xl border ${borderColor} overflow-hidden`}>
                        {/* Summary row */}
                        <div className={`px-4 py-2 flex items-center gap-4 border-b ${borderColor} text-xs ${textSecondary}`}>
                            <span>📊 {hasActiveFilters ? `${filteredSessions.length} / ` : ''}{sessionsTotal} {t.sessions.toLowerCase()}</span>
                            {sessionsLoading && <span className="animate-pulse">⏳</span>}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className={`sticky top-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                    <tr>
                                        <th className={`text-left p-3 ${textColor} w-8`}></th>
                                        <th className={`text-left p-3 ${textColor} w-10`}>#</th>
                                        <th className={`text-left p-3 ${textColor}`}>{t.time}</th>
                                        <th className={`text-center p-3 ${textColor}`}>{t.activity}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.duration}</th>
                                        <th className={`text-right p-3 ${textColor}`}>{t.pages}</th>
                                        <th className={`text-center p-3 ${textColor}`}>{t.device}</th>
                                        <th className={`text-left p-3 ${textColor}`}>{t.page}</th>
                                        <th className={`text-left p-3 ${textColor}`}>{t.source}</th>
                                        <th className={`text-left p-3 ${textColor}`}>{t.location}</th>
                                        <th className={`text-center p-3 ${textColor}`}>{t.replay}</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-gray-100'}`}>
                                    {filteredSessions.map((session, i) => (
                                        <React.Fragment key={`${session.id}-${i}`}>
                                            <tr className={`${hoverBg} transition-colors cursor-pointer`} onClick={() => toggleExpandRow(i, session)}>
                                                <td className="p-3 text-center">
                                                    <span className={`text-xs transition-transform inline-block ${expandedRows.has(i) ? 'rotate-90' : ''}`}>▶</span>
                                                </td>
                                                <td className={`p-3 ${textSecondary} text-xs`}>{session.id}</td>
                                                <td className={`p-3 ${textColor}`}>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{session.visitTime}</span>
                                                        <span className={`text-xs ${textSecondary}`}>{session.visitDate}</span>
                                                    </div>
                                                </td>
                                                <td className="p-3">
                                                    <div className="flex justify-center">{getActivityDots(session.activityLevel)}</div>
                                                </td>
                                                <td className={`p-3 text-right font-medium ${session.duration > 120 ? 'text-teal-400' : textSecondary}`}>
                                                    {formatDuration(session.duration)}
                                                </td>
                                                <td className="p-3 text-right">
                                                    <span className={`inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full text-xs font-bold ${session.pageviews >= 5 ? 'bg-teal-500/20 text-teal-400' :
                                                        session.pageviews >= 3 ? 'bg-blue-500/20 text-blue-400' :
                                                            isDark ? 'bg-slate-600/50 text-gray-300' : 'bg-gray-200 text-gray-600'
                                                        }`}>
                                                        {session.pageviews}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <div className="flex items-center justify-center gap-1" title={`${session.os} / ${session.browser} / ${session.device}`}>
                                                        <span className="text-sm">{getOSIcon(session.os)}</span>
                                                        <span className="text-sm">{getBrowserIcon(session.browser)}</span>
                                                        <span className="text-sm">{getDeviceIcon(session.device)}</span>
                                                    </div>
                                                </td>
                                                <td className={`p-3 ${textColor} max-w-[180px]`}>
                                                    <span className="truncate block text-xs" title={session.startUrl}>
                                                        {session.startUrl.replace(/^https?:\/\//, '')}
                                                    </span>
                                                </td>
                                                <td className={`p-3 text-xs`}>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${session.source === 'organic' ? 'bg-green-500/20 text-green-400' :
                                                        session.source === 'direct' || session.source === 'Direct' ? 'bg-blue-500/20 text-blue-400' :
                                                            session.source === 'referral' ? 'bg-purple-500/20 text-purple-400' :
                                                                session.source === 'social' ? 'bg-pink-500/20 text-pink-400' :
                                                                    'bg-gray-500/20 text-gray-400'
                                                        }`}>{session.source}</span>
                                                </td>
                                                <td className={`p-3 ${textSecondary} text-xs`}>
                                                    <span>📍 {session.city}, {session.country}</span>
                                                </td>
                                                <td className="p-3 text-center">
                                                    <a
                                                        href={`https://metrika.yandex.com/watch/${counterId}/webvisor`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={e => e.stopPropagation()}
                                                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-teal-500/20 text-teal-400 hover:bg-teal-500/40 transition-colors"
                                                        title={t.replay}
                                                    >
                                                        🎥
                                                    </a>
                                                </td>
                                            </tr>

                                            {/* Expanded Row — Pages Timeline */}
                                            {expandedRows.has(i) && (
                                                <tr>
                                                    <td colSpan={11} className={`${isDark ? 'bg-slate-800/50' : 'bg-gray-50'} px-6 py-4`}>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <span className="text-sm">📄</span>
                                                            <h5 className={`text-xs font-bold ${textColor}`}>{t.browsedPages}</h5>
                                                        </div>
                                                        {loadingPages[i] ? (
                                                            <div className={`text-xs ${textSecondary} flex items-center gap-2`}>
                                                                <span className="animate-spin">⏳</span> {t.loadingPages}
                                                            </div>
                                                        ) : (sessionPages[i] && sessionPages[i].length > 0) ? (
                                                            <div className="space-y-1">
                                                                {sessionPages[i].map((page, pi) => (
                                                                    <div key={pi} className={`flex items-center gap-3 py-1.5 px-3 rounded-lg ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}>
                                                                        <div className="flex-shrink-0 flex flex-col items-center">
                                                                            <div className="w-2 h-2 rounded-full bg-teal-500" />
                                                                            {pi < sessionPages[i].length - 1 && <div className="w-0.5 h-4 bg-teal-500/30 mt-0.5" />}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className={`text-xs font-medium ${textColor} truncate`} title={page.url}>{page.url}</div>
                                                                            <div className={`text-xs ${textSecondary} truncate`}>{page.title}</div>
                                                                        </div>
                                                                        <div className={`flex-shrink-0 text-xs ${textSecondary}`}>
                                                                            {page.pageviews} {t.visits.toLowerCase()}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className={`text-xs ${textSecondary}`}>📭 {t.noPages}</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More */}
                        {sessions.length < sessionsTotal && (
                            <div className="p-4 flex justify-center">
                                <button
                                    onClick={() => setSessionsOffset(prev => prev + SESSIONS_LIMIT)}
                                    disabled={sessionsLoading}
                                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${sessionsLoading
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'bg-teal-600 hover:bg-teal-500 text-white shadow-lg hover:shadow-teal-500/25'
                                        }`}
                                >
                                    {sessionsLoading ? '⏳' : `${t.showMore} (${sessions.length}/${sessionsTotal})`}
                                </button>
                            </div>
                        )}

                        {filteredSessions.length === 0 && !sessionsLoading && (
                            <div className={`p-8 text-center ${textSecondary}`}>
                                <span className="text-3xl mb-2 block">📭</span>
                                <p>No sessions found for this period</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default YandexAnalytics;
